import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Star, Award, Camera, Users } from "lucide-react";

const stats = [
  {
    icon: Award,
    value: 10000,
    suffix: "+",
    label: "Surfaces Restored",
    subtext: "Floors, Counters, Showers, Pool Decks, Monuments & Headstones",
  },
  {
    icon: Star,
    value: 5.0,
    suffix: "",
    label: "Perfect Rating",
    subtext: "Across All Platforms",
    isDecimal: true,
  },
  {
    icon: Camera,
    value: 3500,
    suffix: "+",
    label: "Photos & Videos",
    subtext: "Just On Facebook Alone",
  },
  {
    icon: Users,
    value: 100,
    suffix: "+",
    label: "Professionals Trained",
    subtext: "Across US, Canada & Australia",
  },
];

function StatValue({
  value,
  suffix,
  isDecimal = false,
}: {
  value: number;
  suffix: string;
  isDecimal?: boolean;
}) {
  // The finished figure is written straight into the markup, so crawlers and AI
  // tools that never run JavaScript read the real number instead of a zero.
  const display = isDecimal
    ? value.toFixed(1)
    : Math.floor(value).toLocaleString("en-US");

  return (
    <motion.span
      className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-primary"
      whileHover={{ scale: 1.05 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      {display}
      {suffix}
    </motion.span>
  );
}

function SubtextDisplay({ text, inView }: { text: string; inView: boolean }) {
  return (
    <motion.p
      initial={{ opacity: 0, y: 10 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: 0.3 }}
      className="text-xs md:text-sm text-accent/90 font-medium mt-1 max-w-[180px] leading-tight"
    >
      {text}
    </motion.p>
  );
}

function InteractiveLabel({ text, inView }: { text: string; inView: boolean }) {
  return (
    <motion.h3 
      className="text-lg md:text-xl font-display font-semibold text-white mt-2 mb-1 cursor-default"
      whileHover={{ 
        scale: 1.02,
      }}
      transition={{ type: "spring", stiffness: 400, damping: 10 }}
    >
      {text.split('').map((char, i) => (
        <motion.span
          key={i}
          className="inline-block"
          initial={{ opacity: 0, y: 20, rotateX: -90 }}
          animate={inView ? { 
            opacity: 1, 
            y: 0, 
            rotateX: 0,
          } : {}}
          transition={{ 
            duration: 0.5, 
            delay: 0.8 + i * 0.03,
            type: "spring",
            stiffness: 100
          }}
          whileHover={{ 
            y: -3, 
            color: "hsl(40 70% 48%)",
            scale: 1.1,
            transition: { duration: 0.1 }
          }}
        >
          {char === ' ' ? '\u00A0' : char}
        </motion.span>
      ))}
    </motion.h3>
  );
}

export function StatsBar() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      ref={ref}
      className="relative py-16 md:py-20 overflow-hidden"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-r from-secondary via-secondary/95 to-secondary" />
      
      {/* Animated background particles - simplified */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-primary/20 rounded-full"
            initial={{ 
              x: `${(i * 35)}%`, 
              y: "100%",
              opacity: 0 
            }}
            animate={isInView ? { 
              y: "-100%",
              opacity: [0, 0.6, 0],
            } : {}}
            transition={{
              duration: 6 + i,
              repeat: Infinity,
              delay: i * 1.5,
              ease: "linear"
            }}
          />
        ))}
      </div>
      
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-50" />
      <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-accent to-transparent opacity-50" />
      
      <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 opacity-[0.15]" />
      <div className="absolute top-1/2 right-1/4 w-64 h-64 bg-accent/10 rounded-full blur-3xl -translate-y-1/2 opacity-[0.15]" />

      <div className="container-custom relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 30, rotateY: -15 }}
              animate={isInView ? { opacity: 1, y: 0, rotateY: 0 } : {}}
              transition={{ 
                duration: 0.8, 
                delay: index * 0.15,
                type: "spring",
                stiffness: 100
              }}
              whileHover={{ 
                scale: 1.02,
                transition: { duration: 0.2 }
              }}
              className="text-center group perspective-1000"
            >
              <div className="flex flex-col items-center">
                <motion.div 
                  className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-4 group-hover:bg-primary/30 transition-colors relative overflow-hidden"
                  whileHover={{ 
                    scale: 1.15,
                  }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <div className="absolute inset-0 bg-gradient-to-tr from-primary/0 via-primary/10 to-primary/0 rounded-full" />
                  <stat.icon className="w-8 h-8 text-primary relative z-10" />
                </motion.div>
                
                <StatValue
                  value={stat.value}
                  suffix={stat.suffix}
                  isDecimal={stat.isDecimal}
                />
                
                <InteractiveLabel text={stat.label} inView={isInView} />
                
                <SubtextDisplay text={stat.subtext} inView={isInView} />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
